import {
  createActions, asyncInitialState, asyncOnRequest,
  asyncOnSuccess, asyncOnError, asyncSelectors,
  IExtendedState, IDataAction, SuccessAction
} from '../utils';

export interface IDMEData {
  selected: number;
  list: any[];
  schemas: any;
  nextPage?: number;
  prevPage?: number;
  currPage: number;
  pageSize: number;
  totalPages: number;
  removeWhenPrev: number;
  currSearchText: string;
}

export interface IDMEState extends IExtendedState<IDMEData> {
  search_loading?: boolean;
  searchById_loading?: boolean;
  fetchNextPage_loading?: boolean;
  fetchPrevPage_loading?: boolean;
}

export const { types, actions } = createActions(
  {
    setSelected: (DashboardId) => ({ DashboardId }),
    asyncs: {
      search: ({ SearchText, Sort }) => ({ SearchText, Sort, BatchPage: 1 }),
      searchById: (id: number) => id,
      fetchNextPage: ({ Sort, BatchPage }) => ({ Sort, BatchPage }),
      fetchPrevPage: ({ Sort, BatchPage }) => ({ Sort, BatchPage }),
      reset: () => null
    }
  },
  'dashboardMaintenanceEnquiry');

const NOT_SELECTED = -1;
const initialState = asyncInitialState<IDMEData>({
  selected: NOT_SELECTED,
  list: null,
  schemas: null,
  nextPage: null,
  prevPage: null,
  currPage: 1,
  pageSize: 100,
  totalPages: 5,
  removeWhenPrev: 0,
  currSearchText: ''
});

export default (state: IDMEState = initialState, action: IDataAction): IDMEState => {
  switch (action.type) {
    case types.setSelected:
      const selected = action.data.DashboardId;

      return {
        ...state,
        data: { ...state.data, selected }
      };
    case types.search:
      return asyncOnRequest({ ...state, data: { ...state.data, selected: NOT_SELECTED, currSearchText: action.data.SearchText } }, action);
    case types.searchById:
      return asyncOnRequest({ ...state, data: { ...state.data, selected: NOT_SELECTED } }, action);
    case types.fetchNextPage:
    case types.fetchPrevPage:
      action.data.SearchText = state.data.currSearchText;

      return asyncOnRequest(state, action);
    case types.saga.search.success:
    case types.saga.searchById.success:
      return asyncOnSuccess(
        state,
        action,
        (data: IDMEData, successAction: SuccessAction) => {

          const dashboardMaintenanceEnquiry = successAction.payload.records.map((combinedObject) => combinedObject.inlineObject);
          const schemas = successAction.payload.records.map((combinedObject) => combinedObject.schema);

          return {
            ...data,
            list: dashboardMaintenanceEnquiry,
            schemas: schemas,
            nextPage: successAction.payload.nextPage && parseInt(successAction.payload.currPage) + 1,
            prevPage: successAction.payload.prevPage && parseInt(successAction.payload.currPage) - 1,
            currPage: successAction.payload.currPage
          };
        },
        { fetch: true });
    case types.saga.fetchNextPage.success:
      return asyncOnSuccess(
        state,
        action,
        (data: IDMEData, successAction: SuccessAction) => {
          const result = successAction.payload;
          const dashboardMaintenanceEnquiry = result.records.map((combinedObject) => combinedObject.inlineObject);
          const schemas = result.records.map((combinedObject) => combinedObject.schema);
          const newRemoval = result.records.length;
          const newList = data.list.concat(dashboardMaintenanceEnquiry);
          const newSchemas = data.schemas.concat(schemas);

          return {
            ...data,
            list: (newList.length <= data.pageSize * data.totalPages && newList) || newList.slice(newRemoval, newList.length),
            schemas: (newList.length <= data.pageSize * data.totalPages && newSchemas) || newSchemas.slice(newRemoval, newSchemas.length),
            nextPage: result.nextPage && parseInt(result.currPage) + 1,
            prevPage: (result.prevPage && result.currPage > data.totalPages && result.currPage - data.totalPages),
            currPage: result.currPage,
            removeWhenPrev: (newList.length > data.pageSize * data.totalPages && newRemoval) || 0
          };
        },
        { fetch: true });

    case types.saga.fetchPrevPage.success:
      return asyncOnSuccess(
        state,
        action,
        (data: IDMEData, successAction: SuccessAction) => {
          const result = successAction.payload;
          const dashboardMaintenanceEnquiry = result.records.map((combinedObject) => combinedObject.inlineObject);
          const schemas = result.records.map((combinedObject) => combinedObject.schema);
          const newList = dashboardMaintenanceEnquiry.slice(0, data.removeWhenPrev).concat(data.list);
          const newSchemas = schemas.slice(0, data.removeWhenPrev).concat(data.schemas);

          return {
            ...data,
            list: (newList.length <= data.pageSize * data.totalPages && newList) || newList.slice(0, newList.length - data.removeWhenPrev),
            schemas: (newList.length <= data.pageSize * data.totalPages && newSchemas) || newSchemas.slice(0, newSchemas.length - data.removeWhenPrev),
            prevPage: result.prevPage && result.currPage - 1,
            nextPage: (newList.length > data.pageSize * data.totalPages) && (parseInt(result.currPage) + data.totalPages),
            currPage: result.currPage,
            removeWhenPrev: ((newList.length >= data.pageSize * data.totalPages) && data.pageSize) || 0
          };
        },
        { fetch: true });

    case types.saga.search.failure:
    case types.saga.searchById.failure:
    case types.saga.fetchNextPage.failure:
    case types.saga.fetchPrevPage.failure:

      return asyncOnError(state, action);
    case types.reset:
      return initialState;
    default:
      return state;
  }
};

const asyncSelector = asyncSelectors(
  (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards,
  {
    all: (data) => (data.list !== undefined && data.list != null && data.list) || [],
    allSchemas: (data) => data.schemas !== undefined && data.schemas != null && Object.values(data.schemas),
    selected: (data) => (data.list !== undefined && data.list != null && data.list.find((element) => element.DashboardId === data.selected)) || null,
    selectedSchema: (data) => (data.schemas !== undefined && data.schemas != null && data.schemas.find((element) => element.DashboardId.Value === data.selected)) || null,
    nextPage: (data) => data.nextPage,
    prevPage: (data) => data.prevPage,
    selectedDashboardId: (data) => data.selected
  }
);

const syncSelector = {
  isLoading: (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards.search_loading || state.dashboardMaintenanceEnquiry.dashboards.searchById_loading,
  loadingNextPage: (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards.fetchNextPage_loading,
  loadingPrevPage: (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards.fetchPrevPage_loading,
  pageSize: (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards.data.pageSize,
  totalPages: (state: { dashboardMaintenanceEnquiry: { dashboards: IDMEState } }) => state.dashboardMaintenanceEnquiry.dashboards.data.totalPages,
};

export const selectors = { ...asyncSelector, ...syncSelector };
